/**
 * Correcting a project after it exists.
 *
 * The API has allowed this since projects were built and audits every field
 * change; there was simply no way to ask for it, so a typo in a name was
 * permanent. That is the bug this fixes.
 *
 * Deliberately the same fields and the same rules as creating one — the client
 * is required on Client and Proposal work and forbidden on the other two, and
 * an Ajera project is offered only for the chosen client — because a form that
 * lets you edit a thing into a shape you could not have created is a form that
 * produces database refusals nobody could have predicted.
 */
import type { ProjectType, ProjectView, ReferenceOption } from "@fieldpath/shared";
import { PROJECT_TYPES, requiresClient } from "@fieldpath/shared";
import { useEffect, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { SearchSelect } from "./SearchSelect";

interface Props {
  fetcher: Fetcher;
  project: ProjectView;
  onSaved: () => void;
  onCancel: () => void;
  /**
   * Rendered inside a `Modal`, which supplies the frame and the heading
   * (punch list #3, 12 Oct 2026). A card and a second heading inside it would
   * be two borders and two titles saying the same thing.
   */
  inDialog?: boolean;
  /**
   * May change WHO the PM and TM are: the project's PM or an admin, as the
   * server enforces (`mayManageProject`, 12 Oct 2026). Anybody else sees them
   * as text, and the save leaves them out rather than sending them back.
   */
  mayChangeManagers: boolean;
  /**
   * May change its name, type, client and Ajera job: the project's PM or an
   * admin (`mayManageProject`, 22 Oct 2026). False only for a PM naming the PM
   * of a project that has none, who sees them as text and saves only the PM/TM.
   */
  mayChangeDetails: boolean;
}

export function EditProject({
  fetcher,
  project,
  onSaved,
  onCancel,
  inDialog = false,
  mayChangeManagers,
  mayChangeDetails,
}: Props) {
  const [name, setName] = useState(project.name);
  const [projectType, setProjectType] = useState<ProjectType>(project.projectType);
  const [clientKey, setClientKey] = useState<string>(project.clientKey ?? "");
  const [deltekProjectKey, setDeltekProjectKey] = useState<string>(project.deltekProjectKey ?? "");
  const [pmKey, setPmKey] = useState<string>(project.pmKey ?? "");
  const [tmKey, setTmKey] = useState<string>(project.tmKey ?? "");
  const [isTemplate, setIsTemplate] = useState(project.isTemplate);
  // The template flag is an admin's (21 Oct 2026); the server refuses anybody
  // else, so nobody else is shown it.
  const [isAdmin, setIsAdmin] = useState(false);

  const [managers, setManagers] = useState<ReferenceOption[]>([]);
  const [clients, setClients] = useState<ReferenceOption[]>([]);
  const [deltekProjects, setDeltekProjects] = useState<ReferenceOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const needsClient = requiresClient(projectType);

  useEffect(() => {
    void api.clients(fetcher).then(setClients).catch(() => undefined);
    void api.projectManagers(fetcher).then(setManagers).catch(() => undefined);
    void api.amIAdmin(fetcher).then(setIsAdmin).catch(() => setIsAdmin(false));
  }, [fetcher]);

  // Ajera projects are fetched per client, as on creation: the database refuses
  // one whose client disagrees, so offering the rest would only produce a
  // rejection nobody could have seen coming.
  //
  // The existing link is kept when the client has not changed. Clearing it on
  // mount would silently unlink a project just because somebody opened the form
  // to fix a spelling mistake.
  useEffect(() => {
    if (!clientKey) {
      setDeltekProjects([]);
      setDeltekProjectKey("");
      return;
    }
    void api
      .deltekProjects(fetcher, clientKey)
      .then((options) => {
        setDeltekProjects(options);
        setDeltekProjectKey((current) =>
          current && options.some((o) => o.key === current) ? current : "",
        );
      })
      .catch(() => setDeltekProjects([]));
  }, [fetcher, clientKey]);

  const dirty =
    name.trim() !== project.name ||
    projectType !== project.projectType ||
    (clientKey || null) !== (project.clientKey ?? null) ||
    (deltekProjectKey || null) !== (project.deltekProjectKey ?? null) ||
    (pmKey || null) !== (project.pmKey ?? null) ||
    (tmKey || null) !== (project.tmKey ?? null) ||
    isTemplate !== project.isTemplate;

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await api.updateProject(fetcher, project.id, {
        ...(mayChangeDetails
          ? {
              name: name.trim(),
              projectType,
              // Cleared rather than left alone when the type stops allowing one: a
              // check constraint forbids a client on Administrative and Internal
              // work, and sending the old value would be refused.
              clientKey: needsClient && clientKey ? clientKey : null,
              deltekProjectKey: needsClient && deltekProjectKey ? deltekProjectKey : null,
            }
          : {}),
        ...(mayChangeManagers ? { pmKey: pmKey || null, tmKey: tmKey || null } : {}),
        ...(isTemplate !== project.isTemplate ? { isTemplate } : {}),
      });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className={inDialog ? undefined : "card"}
      onSubmit={save}
      style={inDialog ? undefined : { marginBottom: 18 }}
    >
      {inDialog ? null : <h2>Edit project</h2>}
      {error ? <div className="error">{error}</div> : null}

      {mayChangeDetails ? null : (
        <div className="field">
          {/* A label for its look; there is no control here to point it at. */}
          <label>Project</label>
          <p style={{ margin: "4px 0 0" }}>
            {project.name} · {project.projectType}
            {project.clientName ? ` · ${project.clientName}` : ""}
            {project.deltekProjectName ? ` · Ajera: ${project.deltekProjectName}` : ""}
          </p>
          <p className="muted" style={{ marginTop: 4 }}>
            Only this project's PM or an admin can change its name, type, client or Ajera job.
          </p>
        </div>
      )}

      {mayChangeDetails ? (
        <>
          <div className="field">
            <label htmlFor="ep-name">Name</label>
            <input
              id="ep-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="field">
            <label htmlFor="ep-type">Type</label>
            <select
              id="ep-type"
              value={projectType}
              onChange={(e) => setProjectType(e.target.value as ProjectType)}
            >
              {PROJECT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {!needsClient && project.clientKey ? (
              <p className="muted" style={{ marginTop: 4 }}>
                {projectType} work has no client, so saving will clear{" "}
                {project.clientName ?? "the client"}
                {project.deltekProjectName ? " and the Ajera link" : ""}.
              </p>
            ) : null}
          </div>

          {needsClient ? (
            <>
              <div className="field">
                <label htmlFor="ep-client">Client (required for {projectType} work)</label>
                <SearchSelect
                  id="ep-client"
                  options={clients}
                  value={clientKey}
                  onChange={setClientKey}
                  emptyLabel={`Start typing a client — ${clients.length} to choose from`}
                />
              </div>

              <div className="field">
                <label htmlFor="ep-ajera">Ajera project (optional)</label>
                <SearchSelect
                  id="ep-ajera"
                  options={deltekProjects}
                  value={deltekProjectKey}
                  onChange={setDeltekProjectKey}
                  disabled={!clientKey}
                  emptyLabel={
                    clientKey
                      ? `Start typing — ${deltekProjects.length} for this client`
                      : "Choose a client first"
                  }
                />
              </div>
            </>
          ) : (
            <p className="muted">
              {projectType} projects have no client, so they cannot be linked to an Ajera project.
            </p>
          )}
        </>
      ) : null}

      {/* PM and TM, decided 11 Oct (E9). Display and "you manage", and the
          default owner of new deliverables and tasks -- copied when those are
          created, so changing them here does not reach work already made. */}
      {mayChangeManagers ? (
        <>
          <div className="field">
            <label htmlFor="ep-pm">PM</label>
            <PersonSelect
              id="ep-pm"
              options={managers}
              value={pmKey}
              currentName={project.pmName}
              currentKey={project.pmKey}
              onChange={setPmKey}
            />
          </div>

          <div className="field">
            <label htmlFor="ep-tm">TM or additional PM</label>
            <PersonSelect
              id="ep-tm"
              options={managers}
              value={tmKey}
              currentName={project.tmName}
              currentKey={project.tmKey}
              onChange={setTmKey}
            />
            <p className="muted" style={{ marginTop: 4 }}>
              New deliverables and tasks take the PM as their owner. Changing either
              here does not change work already created.
            </p>
          </div>
        </>
      ) : (
        <div className="field">
          {/* A label for its look; there is no control here to point it at. */}
          <label>PM / TM</label>
          <p style={{ margin: "4px 0 0" }}>
            {project.pmName ?? "No PM"} · {project.tmName ?? "no TM"}
          </p>
          <p className="muted" style={{ marginTop: 4 }}>
            Only this project's PM or an admin can change who these are.
          </p>
        </div>
      )}

      {isAdmin ? (
        <div className="field">
          <label>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={isTemplate}
              onChange={(e) => setIsTemplate(e.target.checked)}
            />{" "}
            Template project
          </label>
          <p className="muted" style={{ marginTop: 4 }}>
            Where templates are built. Only the people on Admin › Settings, "Who can see the
            template project", can see it; its work stays off My work, Workload, Overview and
            Needs attention, and it notifies nobody. One open template project at a time.
          </p>
        </div>
      ) : null}

      <div className="row">
        <button
          className="primary"
          type="submit"
          disabled={saving || !dirty || name.trim() === "" || (needsClient && !clientKey)}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button type="button" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
        {dirty ? <span className="muted">Unsaved changes</span> : null}
      </div>
    </form>
  );
}

/**
 * A PM-flagged person, or nobody.
 *
 * **The current choice stays listed even when the list no longer offers it** —
 * somebody who left, or lost the PM flag, after being named. Dropping them
 * would show "Not set" for a project that has a PM, and saving the form for an
 * unrelated typo would then silently clear it. Constrain the choice, not the
 * history.
 */
function PersonSelect({
  id,
  options,
  value,
  currentKey,
  currentName,
  onChange,
}: {
  id: string;
  options: ReferenceOption[];
  value: string;
  currentKey: string | null;
  currentName: string | null;
  onChange: (key: string) => void;
}) {
  const stranded = currentKey && !options.some((o) => o.key === currentKey);
  // Type to find, like every list of people (punch list #27). × clears it
  // to "Not set".
  return (
    <SearchSelect
      id={id}
      options={
        stranded
          ? [{ key: currentKey as ReferenceOption["key"], label:`${currentName ?? currentKey} (no longer offered)` }, ...options]
          : options
      }
      value={value}
      emptyLabel="Not set"
      onChange={onChange}
    />
  );
}
